import { useCallback, useContext, useMemo } from 'react';

import moment from 'moment';

import { type AttendanceData, type ClassSessionView, type SessionSummaryView, type StudentSummaryView, type StudentView } from './types';
import { AttendanceContext } from '../../context/AttendanceContext';
import { getSortName } from '../../utils/sortingUtils';
import { getDisplayName } from '../../utils/student';
import { secondsToMinutes } from '../../utils/timeConversion';

export const useAttendanceData = () => {
    const {
        students,
        studentId,
        sessions,
        classSessionId,
        sessionAttendance,
        studentAttendance,
        classAttendance,
        attendanceCache,
        classInfo
    } = useContext(AttendanceContext);

    const attendanceValue = (attendance: AttendanceData): boolean => {
        if (attendance.marks && attendance.marks.marked_present !== null) return attendance.marks.marked_present;
        return !!(attendance.events && attendance.events.present);
    };

    const tardyValue = (attendance: AttendanceData): boolean => {
        if (attendance.marks && attendance.marks.marked_tardy !== null) return attendance.marks.marked_tardy;
        return !!(attendance.events && attendance.events.tardy);
    };

    const joinTimeValue = (attendance: AttendanceData): string => {
        if (attendance.marks && attendance.marks.marked_join_time) return attendance.marks.marked_join_time;
        if (attendance.events && attendance.events.join_time) return attendance.events.join_time;

        return '';
    };

    const leaveTimeValue = (attendance: AttendanceData): string => {
        if (attendance.marks && attendance.marks.marked_leave_time) return attendance.marks.marked_leave_time;
        if (attendance.events && attendance.events.leave_time) return attendance.events.leave_time;

        return '';
    };

    const durationValue = (attendance: AttendanceData): number => {
        if (attendance.marks && attendance.marks.marked_duration) return attendance.marks.marked_duration;
        if (attendance.events && attendance.events.duration) return attendance.events.duration;

        return 0;
    };

    /**
     * Transform session attendance
     * data into student summary rows
     */
    const buildStudentSummaryRow = useCallback(
        (student): SessionSummaryView => {
            const row = {
                studentId: student.student_id,
                classSessionId: classSessionId as string,
                name: getDisplayName(student),
                sortName: getSortName(student),
                presences: 0,
                absences: 0,
                tardies: 0,
                score: '0' as string
            };

            if (classAttendance.length === 0) return row;
            if (!attendanceCache.current) return row;

            const cachedStudentAttendance = attendanceCache.current[student.student_id];
            if (!cachedStudentAttendance) return row;

            Object.keys(cachedStudentAttendance).forEach((sessionId) => {
                const data = attendanceCache.current[student.student_id][sessionId];

                if (!data) return;

                if (attendanceValue(data)) row.presences += 1;
                if (!attendanceValue(data)) row.absences += 1;
                if (tardyValue(data)) row.tardies += 1;
            });
            // if we have lms sync compute the score
            if (classInfo.lms_sync_settings?.enable_attendance_grade) {
                const lmsAttendanceTardyScore = (classInfo.class_options?.attendanceGradeTardyScore || 80) / 100;
                const totalSessions = classAttendance.length;
                row.score = (
                    ((row.presences - row.tardies + row.tardies * lmsAttendanceTardyScore) / totalSessions) *
                    100
                ).toFixed(2);
            }
            return row;
        },
        [attendanceCache, classAttendance, classSessionId, classInfo]
    );

    /**
     * Transform session attendance
     * data into session summary rows
     */
    const buildSessionSummaryRow = useCallback(
        (session): StudentSummaryView => {
            const row = {
                studentId: studentId as string,
                classSessionId: session.session_id,
                date: moment(session.start_time).format('L - dddd - h:mm A'),
                presences: 0,
                absences: 0,
                tardies: 0,
                score: '0' as string
            };

            if (classAttendance.length === 0) return row;
            if (!attendanceCache.current) return row;

            const sessionReport = classAttendance.find((i) => i.session_id === session.session_id);
            if (!sessionReport) return row;

            const cachedSessionAttendance = attendanceCache.current[session.session_id];
            if (!cachedSessionAttendance) return row;

            Object.keys(cachedSessionAttendance).forEach((student) => {
                const data = attendanceCache.current[session.session_id][student];

                if (!data) {
                    return row;
                }

                if (attendanceValue(data)) row.presences += 1;
                if (!attendanceValue(data)) row.absences += 1;
                if (tardyValue(data)) row.tardies += 1;
            });

            return row;
        },
        [attendanceCache, classAttendance, studentId]
    );

    /**
     * Transform session attendance
     * data into student table rows
     */
    const buildStudentRow = useCallback(
        (student): ClassSessionView => {
            if (sessionAttendance && sessionAttendance[student.student_id]) {
                const data = sessionAttendance[student.student_id];

                const row = {
                    studentId: student.student_id,
                    classSessionId: classSessionId as string,
                    name: getDisplayName(student),
                    sortName: getSortName(student),
                    attendance: attendanceValue(data),
                    tardiness: tardyValue(data),
                    timeIn: joinTimeValue(data),
                    timeOut: leaveTimeValue(data),
                    duration: secondsToMinutes(durationValue(data)),
                    found: true
                };

                // handle absences
                if (!row.attendance) {
                    row.timeIn = '';
                    row.timeOut = '';
                    row.tardiness = false;
                }

                return row;
            }

            return {
                studentId: student.student_id,
                classSessionId: classSessionId as string,
                name: getDisplayName(student),
                sortName: getSortName(student),
                attendance: false,
                tardiness: false,
                timeIn: '',
                timeOut: '',
                duration: '0',
                found: false
            };
        },
        [classSessionId, sessionAttendance]
    );

    /**
     * Transform student attendance
     * data into session table rows
     */
    const buildSessionRow = useCallback(
        (session): StudentView => {
            if (studentAttendance && studentAttendance[session.session_id]) {
                const data = studentAttendance[session.session_id];

                const row = {
                    studentId: studentId as string,
                    classSessionId: session.session_id,
                    date: moment(session.start_time).format('L - dddd'),
                    attendance: attendanceValue(data),
                    tardiness: tardyValue(data),
                    timeIn: joinTimeValue(data),
                    timeOut: leaveTimeValue(data),
                    duration: secondsToMinutes(durationValue(data)),
                    found: true
                };

                // handle absences
                if (!row.attendance) {
                    row.timeIn = '';
                    row.timeOut = '';
                    row.tardiness = false;
                }

                return row;
            }

            return {
                studentId: studentId as string,
                classSessionId: session.session_id,
                date: moment(session.start_time).format('L - dddd'),
                attendance: false,
                tardiness: false,
                timeIn: '',
                timeOut: '',
                duration: '0',
                found: false
            };
        },
        [studentAttendance, studentId]
    );

    const studentRows = useMemo(
        () => (!sessionAttendance ? [] : students.map(buildStudentRow)),
        [buildStudentRow, students, sessionAttendance]
    );
    const sessionRows = useMemo(
        () => (!studentAttendance ? [] : sessions.map(buildSessionRow)),
        [buildSessionRow, sessions, studentAttendance]
    );

    const studentSummaryRows = useMemo(
        () => (!classAttendance ? [] : students.map(buildStudentSummaryRow)),
        [classAttendance, students, buildStudentSummaryRow]
    );
    const sessionSummaryRows = useMemo(
        () => (!classAttendance ? [] : sessions.map(buildSessionSummaryRow)),
        [buildSessionSummaryRow, classAttendance, sessions]
    );

    return { studentRows, sessionRows, studentSummaryRows, sessionSummaryRows };
};
